import React, { useState, useEffect } from 'react';
import { EmptyState } from './shared';

const GRADIENT = 'linear-gradient(90deg, #004AAD 0%, #0082FB 100%)';
const GRADIENT_TEXT = {
  background: GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const MarketAnalysis = ({ marketData, startupData }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>
      <h2 style={{
        fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
        fontWeight: 800, 
        margin: isMobile ? '0 0 14px 0' : '0 0 20px 0',
        color: '#0f172a',
        fontFamily: "'Sansation', sans-serif",
        letterSpacing: '-0.03em'
      }}>
        Market Specific Analysis
      </h2>

      {(marketData?.overview || marketData?.insights?.length) ? (
        <div style={{ 
          background: '#ffffff', 
          borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
          padding: isMobile ? '18px 16px' : (isTablet ? 20 : 24), 
          border: '0.5px solid rgba(0,0,0,0.06)', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Header */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 10 : 14, 
            marginBottom: isMobile ? 16 : 20, 
            paddingBottom: isMobile ? 12 : 16, 
            borderBottom: '0.5px solid rgba(0,0,0,0.06)' 
          }}>
            <div style={{
              width: isMobile ? 36 : 42, 
              height: isMobile ? 36 : 42, 
              borderRadius: isMobile ? 10 : 12,
              background: 'linear-gradient(135deg, rgba(0,74,173,0.10) 0%, rgba(0,130,251,0.10) 100%)',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontSize: isMobile ? 18 : 20,
              flexShrink: 0,
            }}>
              🌐
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: isMobile ? 15 : 17, 
                fontWeight: 700,
                fontFamily: "'Sansation', sans-serif",
                wordBreak: 'break-word',
                ...GRADIENT_TEXT,
              }}>
                {startupData?.industry || 'Market Intelligence'}
              </div>
              <div style={{ 
                fontSize: isMobile ? 11 : 12, 
                color: '#6e6e73', 
                marginTop: 2 
              }}>
                Market Overview
              </div>
            </div>
          </div>

          {/* Overview text */}
          {marketData?.overview && (
            <div style={{
              background: '#f8f8fa', 
              borderRadius: isMobile ? 12 : 16, 
              padding: isMobile ? '14px 16px' : '18px 20px', 
              marginBottom: isMobile ? 16 : 20,
              border: '0.5px solid rgba(0,0,0,0.06)', 
              fontSize: isMobile ? 12.5 : 13.5, 
              color: '#6e6e73', 
              lineHeight: 1.7,
              wordBreak: 'break-word',
            }}>
              {marketData.overview}
            </div>
          )}

          {/* Insight cards */}
          {marketData?.insights?.length > 0 && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: 2, 
              background: 'rgba(0,0,0,0.04)', 
              borderRadius: isMobile ? 12 : 14, 
              overflow: 'hidden',
              width: '100%',
            }}>
              {marketData.insights.map((ins, i) => {
                const title = ins.title || (typeof ins === 'string' ? ins.split(/[.—:]/)[0].trim() : '');
                const body = ins.body || ins.description || (typeof ins === 'string' ? ins.split(/[.—:]/)[1]?.trim() : '');
                const isLast = i === marketData.insights.length - 1;
                const isOdd = marketData.insights.length % 2 !== 0;
                const spanFull = isLast && isOdd && !isMobile;
                
                return (
                  <div key={i} style={{
                    gridColumn: spanFull ? '1 / -1' : 'span 1',
                    background: '#ffffff', 
                    padding: isMobile ? 14 : 18,
                    borderLeft: '3px solid transparent',
                    borderImage: `${GRADIENT} 1`,
                    transition: 'all 0.2s',
                    minWidth: 0,
                  }}>
                    <div style={{ 
                      fontSize: isMobile ? 12 : 13, 
                      fontWeight: 700, 
                      color: '#0f172a', 
                      marginBottom: isMobile ? 5 : 6, 
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                    }}>
                      {title}
                    </div>
                    {body && (
                      <div style={{ 
                        fontSize: isMobile ? 11.5 : 12.5, 
                        color: '#6e6e73', 
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                      }}>
                        {body}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div style={{ 
          background: '#ffffff', 
          borderRadius: isMobile ? 14 : 20, 
          padding: isMobile ? 20 : 28, 
          border: '0.5px solid rgba(0,0,0,0.06)' 
        }}>
          <EmptyState 
            icon="BarChart2" 
            title="No market data" 
            desc="Market specific analysis will populate from your HTML report." 
          />
        </div>
      )}
    </div>
  );
};

export default MarketAnalysis;